import React, { useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useWorkingModel } from "@/hooks/useWorkingModel";
import { PageHeader, RequireModel } from "@/components/PageParts";
import ZoomPan from "@/components/ZoomPan";
import TitleBlock from "@/components/TitleBlock";
import PlanActions from "@/components/PlanActions";
import { Button, Card } from "@/components/ui";

const FOUND_DATUM = -1.2;   // foundation top datum (m below ground)

const Chip = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`rounded-full border px-3 py-1 text-xs font-bold transition-colors ${active ? "border-sky-500 bg-sky-500 text-white" : "border-border bg-card text-foreground hover:border-sky-400"}`}>
    {children}
  </button>
);

/**
 * Facade - real architectural elevation projector (parity with the mobile
 * renderer). Projects every EXTERIOR opening onto the chosen side at the
 * correct sill/head height, draws floor-level lines, a parapet, a per-floor
 * vertical dimension chain, per-opening head/sill dimensions and an overall
 * span dimension so the elevation reads clearly.
 */
function Facade({ model, side, floors, wallH, parapet, showLevels, showOpenings, showOpeningDims, ar }) {
  const bounds = useMemo(() => {
    const walls = model?.walls || [];
    if (!walls.length) return { xmin: 0, xmax: 20, ymin: 0, ymax: 20 };
    let xmin = Infinity, xmax = -Infinity, ymin = Infinity, ymax = -Infinity;
    walls.forEach((w) => {
      xmin = Math.min(xmin, w.p1.x, w.p2.x); xmax = Math.max(xmax, w.p1.x, w.p2.x);
      ymin = Math.min(ymin, w.p1.y, w.p2.y); ymax = Math.max(ymax, w.p1.y, w.p2.y);
    });
    return { xmin, xmax, ymin, ymax };
  }, [model]);

  const projected = useMemo(() => {
    const walls = model?.walls || [];
    const openings = model?.openings || [];
    const wById = new Map();
    walls.forEach((w) => { if (w.id) wById.set(w.id, w); });
    const EPS = 0.20;
    const isFacade = (w) => {
      const isHoriz = Math.abs(w.p1.y - w.p2.y) < 0.01;
      const isVert = Math.abs(w.p1.x - w.p2.x) < 0.01;
      if (side === "north") return isHoriz && Math.abs(w.p1.y - bounds.ymin) < EPS;
      if (side === "south") return isHoriz && Math.abs(w.p1.y - bounds.ymax) < EPS;
      if (side === "west") return isVert && Math.abs(w.p1.x - bounds.xmin) < EPS;
      if (side === "east") return isVert && Math.abs(w.p1.x - bounds.xmax) < EPS;
      return false;
    };
    const spanM = (side === "north" || side === "south")
      ? (bounds.xmax - bounds.xmin) : (bounds.ymax - bounds.ymin);
    const items = [];
    for (const op of openings) {
      const w = op.wall_id ? wById.get(op.wall_id) : undefined;
      if (!w || !isFacade(w)) continue;
      const dx = w.p2.x - w.p1.x, dy = w.p2.y - w.p1.y;
      const len = Math.hypot(dx, dy) || 1;
      const ux = dx / len, uy = dy / len;
      const cxWorld = w.p1.x + ux * (op.offset_from_p1 || 0);
      const cyWorld = w.p1.y + uy * (op.offset_from_p1 || 0);
      let projCentre;
      if (side === "north" || side === "south") {
        projCentre = cxWorld - bounds.xmin;
        if (side === "south") projCentre = (bounds.xmax - bounds.xmin) - projCentre;
      } else {
        projCentre = cyWorld - bounds.ymin;
        if (side === "east") projCentre = (bounds.ymax - bounds.ymin) - projCentre;
      }
      const widthM = op.width || 0.9;
      const heightM = op.height || (op.kind === "door" ? 2.10 : 1.20);
      const sillBase = op.sill_m ?? (op.kind === "door" ? 0.0 : 0.90);
      for (let f = 0; f < floors; f++) {
        items.push({ xM: projCentre - widthM / 2, widthM, sillM: f * wallH + sillBase, heightM, kind: op.kind });
      }
    }
    return { spanM: Math.max(4, spanM || 12), openings: items };
  }, [model, side, bounds, floors, wallH]);

  const totalHeight = floors * wallH + parapet;
  const spanM = projected.spanM;
  const scale = 640 / Math.max(spanM, totalHeight);
  const padLeft = 74, padBottom = 60, padRight = 70, padTop = 30;
  const svgW = spanM * scale + padLeft + padRight;
  const svgH = totalHeight * scale + padBottom + padTop + 40;
  const X = (m) => padLeft + m * scale;
  const Y = (m) => svgH - padBottom - m * scale;
  const parapetTop = floors * wallH + parapet;

  // vertical dimension chain stops: foundation → each floor → parapet
  const chainStops = [FOUND_DATUM, 0];
  for (let f = 1; f <= floors; f++) chainStops.push(f * wallH);
  chainStops.push(parapetTop);

  return (
    <svg width={svgW} height={svgH} viewBox={`0 0 ${svgW} ${svgH}`} style={{ background: "#fff" }} xmlns="http://www.w3.org/2000/svg">
      {/* sky + ground */}
      <rect x={0} y={0} width={svgW} height={Y(0)} fill="#E0F2FE" opacity={0.35} />
      <rect x={0} y={Y(0)} width={svgW} height={svgH - Y(0)} fill="#78716C" opacity={0.15} />
      <line x1={0} y1={Y(0)} x2={svgW} y2={Y(0)} stroke="#78716C" strokeWidth={1.5} />

      {/* foundation datum */}
      <line x1={padLeft - 12} y1={Y(FOUND_DATUM)} x2={X(spanM) + 10} y2={Y(FOUND_DATUM)} stroke="#94A3B8" strokeDasharray="6 4" strokeWidth={1} />
      <text x={padLeft - 16} y={Y(FOUND_DATUM) + 4} textAnchor="end" fontSize={10} fill="#64748B" fontWeight="700">{FOUND_DATUM.toFixed(2)}</text>

      {/* building silhouette */}
      <rect x={X(0)} y={Y(parapetTop)} width={spanM * scale} height={parapetTop * scale} fill="#F5F5F4" stroke="#0F172A" strokeWidth={2.5} />

      {/* plinth / base course (جلسة) — anchors the ground floor visually */}
      <rect x={X(0)} y={Y(0.45)} width={spanM * scale} height={0.45 * scale} fill="#D6D3D1" stroke="#0F172A" strokeWidth={1} />

      {/* floor lines + level tags */}
      {showLevels && chainStops.slice(1).map((z, i) => (
        <g key={`fl-${i}`}>
          <line x1={X(0)} y1={Y(z)} x2={X(spanM)} y2={Y(z)} stroke="#0F172A" strokeWidth={z === 0 ? 2 : 1} strokeDasharray={z === 0 ? "" : "3 3"} />
          <text x={X(spanM) + 6} y={Y(z) + 4} fontSize={10} fill="#0F172A" fontWeight="800">+{z.toFixed(2)}</text>
        </g>
      ))}
      {/* floor band labels (الدور الأرضي / الأول …) for readability */}
      {showLevels && Array.from({ length: floors }, (_, f) => {
        const midY = Y((f + 0.5) * wallH);
        const nm = ar
          ? (f === 0 ? "الدور الأرضي" : `الدور ${["الأول", "الثاني", "الثالث", "الرابع", "الخامس"][f - 1] || (f + 1)}`)
          : (f === 0 ? "Ground Floor" : `Floor ${f}`);
        return (
          <text key={`band-${f}`} x={X(spanM / 2)} y={midY} fontSize={12} fill="#57534E" fontWeight="800" textAnchor="middle" opacity={0.5}>{nm}</text>
        );
      })}
      {/* parapet cap */}
      <rect x={X(0) - 3} y={Y(parapetTop) - 6} width={spanM * scale + 6} height={6} fill="#0F172A" />

      {/* openings */}
      {showOpenings && projected.openings.map((o, i) => {
        const isDoor = o.kind === "door";
        const color = isDoor ? "#7C2D12" : "#0369A1";
        const fill = isDoor ? "#78350F" : "#DBEAFE";
        return (
          <g key={`op-${i}`}>
            <rect x={X(o.xM)} y={Y(o.sillM + o.heightM)} width={o.widthM * scale} height={o.heightM * scale} fill={fill} stroke={color} strokeWidth={1.5} rx={1} />
            {!isDoor && (
              <>
                <line x1={X(o.xM + o.widthM / 2)} y1={Y(o.sillM + o.heightM)} x2={X(o.xM + o.widthM / 2)} y2={Y(o.sillM)} stroke={color} strokeWidth={1} />
                <line x1={X(o.xM)} y1={Y(o.sillM + o.heightM / 2)} x2={X(o.xM + o.widthM)} y2={Y(o.sillM + o.heightM / 2)} stroke={color} strokeWidth={1} />
              </>
            )}
            {/* head + sill height dimension (from ground) */}
            {showOpeningDims && o.widthM * scale > 22 && (
              <>
                <text x={X(o.xM + o.widthM / 2)} y={Y(o.sillM + o.heightM) - 4} fontSize={8} fill={color} fontWeight="800" textAnchor="middle" style={{ paintOrder: "stroke", stroke: "#fff", strokeWidth: 2.5 }}>
                  {o.widthM.toFixed(2)}×{o.heightM.toFixed(2)}
                </text>
                <text x={X(o.xM) - 3} y={Y(o.sillM + o.heightM) + 3} fontSize={7.5} fill="#0F172A" fontWeight="700" textAnchor="end" style={{ paintOrder: "stroke", stroke: "#fff", strokeWidth: 2 }}>
                  {(o.sillM + o.heightM).toFixed(2)}
                </text>
                {!isDoor && (
                  <text x={X(o.xM) - 3} y={Y(o.sillM) + 3} fontSize={7.5} fill="#0F172A" fontWeight="700" textAnchor="end" style={{ paintOrder: "stroke", stroke: "#fff", strokeWidth: 2 }}>
                    {o.sillM.toFixed(2)}
                  </text>
                )}
              </>
            )}
          </g>
        );
      })}

      {/* vertical DIMENSION CHAIN (left): foundation + each storey + parapet */}
      {(() => {
        const cx = padLeft - 40;
        const els = [];
        for (let i = 0; i < chainStops.length - 1; i++) {
          const z0 = chainStops[i], z1 = chainStops[i + 1];
          const y0 = Y(z0), y1 = Y(z1);
          els.push(<line key={`c-${i}`} x1={cx} y1={y0} x2={cx} y2={y1} stroke="#1E3A5F" strokeWidth={1} />);
          els.push(<line key={`ct0-${i}`} x1={cx - 4} y1={y0} x2={cx + 4} y2={y0} stroke="#1E3A5F" strokeWidth={1} />);
          els.push(<line key={`ct1-${i}`} x1={cx - 4} y1={y1} x2={cx + 4} y2={y1} stroke="#1E3A5F" strokeWidth={1} />);
          const my = (y0 + y1) / 2;
          els.push(
            <text key={`cl-${i}`} x={cx - 6} y={my} fontSize={9} fill="#1E3A5F" fontWeight="800" textAnchor="middle" transform={`rotate(-90, ${cx - 6}, ${my})`}>
              {(z1 - z0).toFixed(2)}
            </text>
          );
        }
        // overall total on the far left
        const tx = padLeft - 62, y0 = Y(FOUND_DATUM), y1 = Y(parapetTop), my = (y0 + y1) / 2;
        els.push(<line key="tot" x1={tx} y1={y0} x2={tx} y2={y1} stroke="#DC2626" strokeWidth={1.3} />);
        els.push(<text key="totl" x={tx - 7} y={my} fontSize={10} fill="#DC2626" fontWeight="900" textAnchor="middle" transform={`rotate(-90, ${tx - 7}, ${my})`}>{(parapetTop - FOUND_DATUM).toFixed(2)} م</text>);
        return els;
      })()}

      {/* bottom span dimension */}
      <line x1={X(0)} y1={Y(0) + 30} x2={X(spanM)} y2={Y(0) + 30} stroke="#1E3A5F" strokeWidth={1} />
      <line x1={X(0)} y1={Y(0) + 26} x2={X(0)} y2={Y(0) + 34} stroke="#1E3A5F" strokeWidth={1} />
      <line x1={X(spanM)} y1={Y(0) + 26} x2={X(spanM)} y2={Y(0) + 34} stroke="#1E3A5F" strokeWidth={1} />
      <text x={X(spanM / 2)} y={Y(0) + 46} fontSize={11} fill="#1E3A5F" fontWeight="900" textAnchor="middle">
        {(side === "north" || side === "south") ? "الواجهة" : "العمق"} {spanM.toFixed(2)} م
      </text>

      {/* legend (مفتاح الرموز): door / window */}
      <g>
        <rect x={X(0)} y={padTop - 18} width={12} height={12} fill="#78350F" stroke="#7C2D12" strokeWidth={1} rx={1} />
        <text x={X(0) + 16} y={padTop - 8} fontSize={9} fill="#0F172A" fontWeight="800">{ar ? "باب" : "Door"}</text>
        <rect x={X(0) + 52} y={padTop - 18} width={12} height={12} fill="#DBEAFE" stroke="#0369A1" strokeWidth={1} rx={1} />
        <text x={X(0) + 68} y={padTop - 8} fontSize={9} fill="#0F172A" fontWeight="800">{ar ? "نافذة" : "Window"}</text>
      </g>
    </svg>
  );
}

export default function Elevations() {
  const { lang, user } = useAuth();
  const isAr = lang === "ar";
  const [wm] = useWorkingModel();
  const [side, setSide] = useState("north");
  const [floors, setFloors] = useState(wm?.floors || 1);
  const [wallH, setWallH] = useState(3.2);
  const [parapet, setParapet] = useState(0.9);
  const [showLevels, setShowLevels] = useState(true);
  const [showOpenings, setShowOpenings] = useState(true);
  const [showOpeningDims, setShowOpeningDims] = useState(true);
  const [showTB, setShowTB] = useState(true);

  const sideLabelsMap = {
    north: isAr ? "الواجهة الشمالية (أمامية)" : "North elevation (front)",
    south: isAr ? "الواجهة الجنوبية (خلفية)" : "South elevation (rear)",
    east: isAr ? "الواجهة الشرقية" : "East elevation",
    west: isAr ? "الواجهة الغربية" : "West elevation",
  };

  const sides = [
    ["north", isAr ? "شمال (أمامية)" : "North (front)"],
    ["south", isAr ? "جنوب (خلفية)" : "South (rear)"],
    ["east", isAr ? "شرق" : "East"],
    ["west", isAr ? "غرب" : "West"],
  ];

  return (
    <RequireModel>
      {() => (
        <div className="mx-auto max-w-6xl px-4 py-6">
          <PageHeader
            eyebrow={isAr ? "الواجهات" : "Elevations"}
            title={isAr ? "الواجهات المعمارية" : "Architectural elevations"}
            subtitle={isAr ? "الواجهات الأربع بفتحاتها الحقيقية ومناسيب الأدوار وسلاسل قياس الارتفاع." : "Four facades with real openings, floor levels and height dimension chains."}
            right={
              <div className="flex flex-wrap gap-2">
                {sides.map(([k, l]) => (
                  <Button key={k} size="sm" variant={side === k ? "primary" : "outline"} onClick={() => setSide(k)} data-testid={`elev-${k}`}>{l}</Button>
                ))}
              </div>
            }
          />
          <PlanActions
            model={wm.model}
            projectName={wm.title}
            sheet="elevations"
            sheetMeta={{ floors, wall_height_m: wallH, parapet_height_m: parapet }}
            testIdPrefix="elev-actions"
          />

          {/* per-plan edit toolbar - live recompute */}
          <Card className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-2 p-3" data-testid="elev-toolbar">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-muted-foreground">{isAr ? "طوابق:" : "Floors:"}</span>
              {[1, 2, 3, 4, 5].map((f) => <Chip key={f} active={floors === f} onClick={() => setFloors(f)} testid={`elev-floors-${f}`}>{f}</Chip>)}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-muted-foreground">{isAr ? "ارتفاع الدور:" : "Wall H:"}</span>
              {[2.7, 3.0, 3.2, 3.6].map((h) => <Chip key={h} active={wallH === h} onClick={() => setWallH(h)} testid={`elev-wallh-${h}`}>{h}م</Chip>)}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-muted-foreground">{isAr ? "سور السطح:" : "Parapet:"}</span>
              {[0.6, 0.9, 1.2].map((p) => <Chip key={p} active={parapet === p} onClick={() => setParapet(p)} testid={`elev-parapet-${p}`}>{p}م</Chip>)}
            </div>
            <div className="flex items-center gap-2">
              <Chip active={showLevels} onClick={() => setShowLevels((v) => !v)} testid="elev-toggle-levels">{isAr ? "المناسيب" : "Levels"}</Chip>
              <Chip active={showOpenings} onClick={() => setShowOpenings((v) => !v)} testid="elev-toggle-openings">{isAr ? "الفتحات" : "Openings"}</Chip>
              <Chip active={showOpeningDims} onClick={() => setShowOpeningDims((v) => !v)} testid="elev-toggle-opening-dims">{isAr ? "أبعاد الفتحات" : "Opening dims"}</Chip>
              <Chip active={showTB} onClick={() => setShowTB((v) => !v)} testid="toggle-title-block">{isAr ? "بلوك العنوان" : "Title block"}</Chip>
            </div>
          </Card>

          <Card className="p-3">
            <ZoomPan className="h-[560px]">
              <div className="min-w-[600px] p-6">
                <Facade model={wm.model} side={side} floors={floors} wallH={wallH} parapet={parapet}
                  showLevels={showLevels} showOpenings={showOpenings} showOpeningDims={showOpeningDims} ar={isAr} />
              </div>
            </ZoomPan>
          </Card>

          {showTB && (
            <TitleBlock isAr={isAr} projectName={wm?.title} sheetTitle={sideLabelsMap[side]} sheetNo="A-10" scale="1:100" designCode={wm?.design_code} firm={user?.firm_profile} />
          )}
        </div>
      )}
    </RequireModel>
  );
}
